/**
 * useExecutionNotifications — Global toast + sound feedback on execution state transitions.
 *
 * Call from a high-level component (e.g. TopNavBar) with the `active` list produced
 * by useActiveExecutions. It detects:
 *   - queued → processing  → info toast
 *   - disappears from active → GET /executions/{id} → success/failure toast + pin sound
 *
 * Ignores the very first snapshot so refreshes don't spam old jobs.
 */
import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { playPin } from '../utils/soundPin';
import { useWorkflowStore } from '../store/workflowStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LABELS = {
  enhance_photo: 'Mejorar Imagen',
  create_video: 'Crear Video',
  video_with_sound: 'Video con Sonido',
  fabric_avatar: 'Avatar Parlante',
  dream_actor: 'DreamActor',
  kling_avatar: 'Avatar V2 Kling',
  nano_banana: 'Nano Banana',
  background_removal: 'Remover Fondo',
  style_transfer: 'Style Transfer',
  upscale: 'Upscale',
  lyria2: 'Lyria 2',
  qwen3_tts: 'Qwen3 TTS',
  frame_extractor: 'Frame Extractor',
  lyria3_pro: 'Lyria 3 Full Song',
  image_edit: 'Image Edit',
};

const labelOf = (t) => LABELS[t] || t || 'Ejecución';

export function useExecutionNotifications(activeList) {
  const prevStatusRef = useRef(new Map());    // id → last seen status ('queued' | 'processing')
  const notifiedFinalRef = useRef(new Set()); // ids already notified as completed/failed
  const firstRunRef = useRef(true);

  useEffect(() => {
    if (!Array.isArray(activeList)) return;

    const prev = prevStatusRef.current;
    const currentIds = new Set();
    const isFirst = firstRunRef.current;

    // Detect queued → processing transitions and refresh tracked status
    for (const item of activeList) {
      currentIds.add(item.id);
      const prevStatus = prev.get(item.id);
      if (!isFirst && prevStatus === 'queued' && item.status === 'processing') {
        toast.info(`${labelOf(item.result_type)} — empezó a procesar`, {
          duration: 2500,
          position: 'bottom-right',
        });
      }
      prev.set(item.id, item.status);
    }

    // Items that were active in the previous snapshot but are no longer in the active list
    const disappeared = [];
    for (const [id] of prev) {
      if (!currentIds.has(id)) disappeared.push(id);
    }
    for (const id of disappeared) prev.delete(id);

    if (isFirst) {
      firstRunRef.current = false;
      return;
    }

    // For each disappeared execution, fetch its final status and notify
    for (const id of disappeared) {
      if (notifiedFinalRef.current.has(id)) continue;
      notifiedFinalRef.current.add(id);

      axios
        .get(`${API}/executions/${id}`)
        .then((res) => {
          const exec = res?.data?.execution;
          if (!exec) return;
          const name = labelOf(exec.result_type);
          if (exec.status === 'completed') {
            toast.success(`${name} — completado`, {
              duration: 3500,
              position: 'bottom-right',
            });
            playPin('success');
            // Envío LMX-4 (I-26): si es del proyecto abierto, el nodo muestra
            // el resultado sin recargar la página (merge a 3 vías, seguro).
            const ws = useWorkflowStore.getState();
            if (exec.project_id && exec.project_id === ws.currentProjectId) {
              ws.syncFromAgent?.();
              // Envío LMX-6c: si la lanzó LMX / MCP, el orquestador escribe el
              // resultado en su próxima vuelta (cada 3 s): segundo intento.
              const pid = exec.project_id;
              setTimeout(() => {
                const w = useWorkflowStore.getState();
                if (w.currentProjectId === pid) w.syncFromAgent?.();
              }, 4000);
            }
          } else if (exec.status === 'failed') {
            const tail = exec.error_message ? `: ${String(exec.error_message).slice(0, 80)}` : '';
            toast.error(`${name} — falló${tail}`, {
              duration: 5000,
              position: 'bottom-right',
            });
            playPin('failure');
          }
        })
        .catch(() => {
          // silently ignore — worst case user misses a single toast
        });
    }

    // Prune notifiedFinal set to prevent unbounded growth across long sessions
    if (notifiedFinalRef.current.size > 200) {
      const arr = [...notifiedFinalRef.current];
      notifiedFinalRef.current = new Set(arr.slice(-100));
    }
  }, [activeList]);
}

export default useExecutionNotifications;
